import { BsImage } from "react-icons/bs";
import {
  FiArrowLeft,
  FiHash,
  FiSlash,
  FiThumbsDown,
  FiThumbsUp,
  FiTrash2,
} from "react-icons/fi";
import type { ParsedThreadDetailPost } from "@/entities/post";
import {
  addNgImageFromAttachment,
  useNgStore,
} from "@/features/ng-filter/stores";
import { useSettingsStore } from "@/features/settings/hooks";
import {
  type DeleteBulkResult,
  useDeleteMutation,
} from "@/shared/hooks/useDeleteMutation";
import { useDelMutation } from "@/shared/hooks/useDelMutation";
import { Modal } from "@/shared/ui/overlay";
import { useSoudaneMutation } from "../hooks/useSoudaneMutation";
import { useReplyModalStore } from "../stores/replyModalStore";

interface PostActionMenuProps {
  isOpen: boolean;
  onClose: () => void;
  post: ParsedThreadDetailPost;
  /** 親スレッドID。新APIの reactions エンドポイントが要求するため必須化 */
  threadId: string;
  onJumpToPost?: () => void;
}

interface ActionItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  variant?: "default" | "destructive";
}

type QuoteType = "body" | "no-and-filename";

export const PostActionMenu: React.FunctionComponent<PostActionMenuProps> = ({
  isOpen,
  onClose,
  post,
  threadId,
}: PostActionMenuProps) => {
  const openReplyModal = useReplyModalStore((s) => s.open);
  const { addNgWord } = useNgStore();
  const { mutate: soudane } = useSoudaneMutation();
  const { mutate: del } = useDelMutation();
  const { mutate: deletePostOrThread } = useDeleteMutation();
  const { deleteKey } = useSettingsStore();

  // 引用テキストを生成
  const getQuoteText = (type: QuoteType): string => {
    // 新APIでは attachments は配列。最初の1件のみ参照する。
    // TODO(Task 7+): 複数添付対応に拡張する。
    const firstAttachment = post.attachments?.[0] ?? null;
    const attachmentName = firstAttachment?.url?.split("/").pop() ?? null;
    if (type === "no-and-filename") {
      return attachmentName
        ? `>No.${post.postNumber}\n>${attachmentName}`
        : `>No.${post.postNumber}`;
    } else {
      // 本文引用の場合
      return post.body.map((line) => `>${line.text}`).join("\n");
    }
  };

  // 返信モーダルを開く共通処理
  const handleReply = (type: QuoteType): void => {
    openReplyModal(getQuoteText(type));
    onClose();
  };

  // 本文NGハンドラー
  const handleNgBody = (): void => {
    const plainText = post.plainBody.trim();
    addNgWord(plainText);
    alert("本文をNGワードに追加しました");
    onClose();
  };
  // 新APIでは attachments は配列。最初の1件のみ NG 対象とする。
  // TODO(Task 7+): 複数添付対応に拡張する。
  const attachment = post.attachments?.[0] ?? null;

  const actions: ActionItem[] = [
    {
      icon: FiArrowLeft,
      label: "本文返信",
      onClick: () => handleReply("body"),
    },
    {
      icon: FiHash,
      label: "No＆添付名返信",
      onClick: () => handleReply("no-and-filename"),
    },
    {
      icon: FiThumbsUp,
      label: "そうだね",
      onClick: () => {
        soudane({ threadId, postNumber: post.postNumber });
        onClose();
      },
    },
    {
      icon: FiTrash2,
      label: "削除",
      variant: "destructive" as const,
      onClick: () => {
        deletePostOrThread(
          {
            password: deleteKey,
            targets: [
              {
                kind: "post",
                threadId,
                postNumber: post.postNumber,
              },
            ],
          },
          {
            onSuccess: (result: DeleteBulkResult) => {
              if (result.failed.length > 0) {
                alert("レス削除に失敗しました");
              } else {
                alert("レスを削除しました");
              }
            },
            onError: (error: unknown) => {
              alert(
                error instanceof Error ? error.message : "削除に失敗しました",
              );
            },
          },
        );
        onClose();
      },
    },
    {
      icon: FiSlash,
      label: "本文NG",
      variant: "destructive" as const,
      onClick: handleNgBody,
    },
    // 画像NG
    {
      icon: BsImage,
      label: "画像NG",
      variant: "destructive" as const,
      onClick: attachment
        ? () => {
            addNgImageFromAttachment(attachment);
            onClose();
          }
        : undefined,
    },
    {
      icon: FiThumbsDown,
      label: "del",
      variant: "destructive" as const,
      onClick: () => {
        del({ threadId, postNumber: post.postNumber });
        onClose();
      },
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="アクション"
      position="bottom"
    >
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3">
          {actions.map((action) => (
            <button
              type="button"
              key={action.label}
              disabled={!action.onClick}
              onClick={action.onClick}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
            >
              <action.icon
                className={
                  action.variant === "destructive"
                    ? "w-6 h-6 text-destructive"
                    : "w-6 h-6 text-primary"
                }
              />
              <span className="text-sm text-foreground">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
};
